// Chip — the spec's several pill/tag shapes as one component.
//  · spec     : pod attribute tags (RH, CS2, Astron) on --surface2
//  · select   : selectable option (teams, flowlines, theme/accent/language)
//  · status   : 9px uppercase status pill on --soft
//  · stage    : white label on a state colour (issue stages)

import type { CSSProperties, ReactNode } from 'react';

export type ChipKind = 'spec' | 'select' | 'status' | 'stage';
export type ChipTone = 'accent' | 'danger' | 'warn' | 'pos' | 'sub';

const TONE_VAR: Record<ChipTone, string> = {
  accent: 'var(--accent)',
  danger: 'var(--danger)',
  warn: 'var(--warn)',
  pos: 'var(--pos)',
  sub: 'var(--sub)',
};

export interface ChipProps {
  kind?: ChipKind;
  selected?: boolean;
  tone?: ChipTone;
  onClick?: () => void;
  style?: CSSProperties;
  children: ReactNode;
}

export function Chip({ kind = 'spec', selected = false, tone = 'accent', onClick, style, children }: ChipProps) {
  let base: CSSProperties;
  switch (kind) {
    case 'select':
      base = {
        // A chip you can tap is a control, so it gets a control-sized target.
        minHeight: 44,
        padding: '0 16px',
        borderRadius: 22,
        font: '600 12.5px Manrope,sans-serif',
        background: selected ? TONE_VAR[tone] : 'var(--surface2)',
        color: selected ? (tone === 'accent' ? 'var(--accentInk)' : '#fff') : 'var(--ink)',
      };
      break;
    case 'status':
      base = {
        padding: '3px 7px',
        borderRadius: 6,
        font: "700 9px 'Space Grotesk',sans-serif",
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        background: 'var(--soft)',
        color: 'var(--ink)',
      };
      break;
    case 'stage':
      base = {
        padding: '3px 8px',
        borderRadius: 6,
        font: '700 9.5px Manrope,sans-serif',
        letterSpacing: '.03em',
        textTransform: 'uppercase',
        background: TONE_VAR[tone],
        color: tone === 'accent' ? 'var(--accentInk)' : '#fff',
      };
      break;
    case 'spec':
    default:
      base = {
        padding: '5px 9px',
        borderRadius: 8,
        font: '600 11px Manrope,sans-serif',
        background: 'var(--surface2)',
        color: 'var(--ink)',
      };
  }

  const merged: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, ...base, ...style };

  if (!onClick) return <span style={merged}>{children}</span>;
  return (
    <button type="button" className="press" onClick={onClick} aria-pressed={selected} style={merged}>
      {children}
    </button>
  );
}
